/**
 * Phase B1 follow-up #3 — Bulk billing-schedule backfill page.
 * super_admin only (route gate + page-level redirect). Lists unscheduled
 * eligible rentals (default 365-day window) and lets admins bulk-create
 * default monthly schedules. Schedules default to ``starts_at = tomorrow``
 * so operators have lead time to verify before the cron fires.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, AlertTriangle, Loader2, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EQUIPMENT_LABEL = {
    dry_van: "Dry van",
    reefer: "Reefer",
    flatbed: "Flatbed",
    stepdeck: "Stepdeck",
    chassis: "Chassis",
    container: "Container",
};

function _fmtMoney(rate) {
    if (rate === null || rate === undefined) return "—";
    return Number(rate).toLocaleString("en-US", {
        style: "currency", currency: "USD", maximumFractionDigits: 0,
    });
}

function _fmtDate(iso) {
    if (!iso) return "—";
    return String(iso).slice(0, 10);
}

export default function AdminBillingBackfillPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [windowDays, setWindowDays] = useState("365");
    const [q, setQ] = useState("");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [prorateFirst, setProrateFirst] = useState(false);
    const [startsAtOffsetDays, setStartsAtOffsetDays] = useState(1);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const LIMIT = 50;

    // RBAC — super_admin only.
    useEffect(() => {
        if (user && user.role !== "super_admin") {
            toast.error("Bulk schedule backfill is a super-admin tool.");
            navigate("/admin/rentals?has_schedule=false", { replace: true });
        }
    }, [user, navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                window_days: String(windowDays),
                limit: String(LIMIT),
                offset: String(offset),
            });
            if (q) params.set("q", q);
            const { data } = await api.get(
                `/admin/billing/unscheduled-rentals?${params.toString()}`,
            );
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [windowDays, offset, q]);

    useEffect(() => { load(); }, [load]);

    const allSelectedOnPage = useMemo(
        () => items.length > 0 && items.every((r) => selectedIds.has(r.rental_id)),
        [items, selectedIds],
    );
    const toggleAllOnPage = () => {
        const next = new Set(selectedIds);
        if (allSelectedOnPage) {
            items.forEach((r) => next.delete(r.rental_id));
        } else {
            items.forEach((r) => next.add(r.rental_id));
        }
        setSelectedIds(next);
    };
    const selectAllInResult = async () => {
        // Pull every page id up to ``total`` and merge — capped at 500 to
        // match the backend's payload limit.
        if (total > 500) {
            toast.warning(`Bulk creates are capped at 500 rentals per submission. Showing the first 500 of ${total}.`);
        }
        setLoading(true);
        try {
            const next = new Set(selectedIds);
            const pageSize = 200;
            const cap = Math.min(total, 500);
            for (let off = 0; off < cap; off += pageSize) {
                const params = new URLSearchParams({
                    window_days: String(windowDays),
                    limit: String(pageSize), offset: String(off),
                });
                if (q) params.set("q", q);
                const { data } = await api.get(
                    `/admin/billing/unscheduled-rentals?${params.toString()}`,
                );
                (data.items || []).forEach((r) => next.add(r.rental_id));
                if ((data.items || []).length < pageSize) break;
            }
            setSelectedIds(next);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    const toggleOne = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    };

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/billing/backfill", {
                rental_ids: Array.from(selectedIds),
                prorate_first_period: prorateFirst,
                starts_at_offset_days: Number(startsAtOffsetDays),
            });
            const failed = (data.failed || []).length;
            if (failed > 0) {
                toast.warning(`Created ${data.created}. ${failed} failed — see console.`);
                // eslint-disable-next-line no-console
                console.warn("Backfill failures:", data.failed);
            } else {
                toast.success(`Created ${data.created} billing schedule${data.created === 1 ? "" : "s"}.`);
            }
            setSelectedIds(new Set());
            setConfirmOpen(false);
            await load();
        } catch (e) {
            const err = e?.response?.data?.detail;
            if (err && typeof err === "object" && err.error === "already_scheduled") {
                toast.error(`${err.conflicting_rental_ids.length} rental(s) already have a schedule. Refresh and try again.`);
                await load();
            } else {
                toast.error(formatApiError(e));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const targetStartDate = useMemo(() => {
        const d = new Date();
        d.setUTCDate(d.getUTCDate() + Number(startsAtOffsetDays || 0));
        return d.toISOString().slice(0, 10);
    }, [startsAtOffsetDays]);

    if (!user || user.role !== "super_admin") return null;

    return (
        <DashboardShell>
            <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <p className="text-xs font-mono uppercase tracking-wide text-slate-500">
                            Phase B1 · Bulk operation
                        </p>
                        <h1 className="text-2xl font-semibold text-slate-900 mt-1">
                            Backfill billing schedules
                        </h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                            Bulk-create default monthly billing schedules for active rentals
                            that have no recurring billing on file. Schedules are created with{" "}
                            <span className="font-mono text-xs bg-slate-100 px-1 rounded">auto_charge</span>{" "}
                            <span className="font-semibold">off</span> by default — enable auto-charge
                            per schedule after confirming payment methods.
                        </p>
                    </div>
                    <Link
                        to="/admin?tab=today"
                        className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                    >
                        ← Back to dashboard
                    </Link>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                        <Input
                            data-testid="backfill-search"
                            placeholder="Search unit #, VIN, plate, make, model…"
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setOffset(0); }}
                            className="pl-8 h-9"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Label htmlFor="window-select" className="text-xs text-slate-600">Window</Label>
                        <Select
                            value={windowDays}
                            onValueChange={(v) => { setWindowDays(v); setOffset(0); }}
                        >
                            <SelectTrigger id="window-select" data-testid="backfill-window" className="h-9 w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                                <SelectItem value="365">Last 365 days</SelectItem>
                                <SelectItem value="0">All time</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <Button
                            type="button" variant="outline" size="sm"
                            onClick={toggleAllOnPage}
                            disabled={items.length === 0}
                            data-testid="backfill-select-page"
                        >
                            {allSelectedOnPage ? "Deselect page" : "Select page"}
                        </Button>
                        <Button
                            type="button" variant="outline" size="sm"
                            onClick={selectAllInResult}
                            disabled={total === 0 || loading}
                            data-testid="backfill-select-all"
                        >
                            Select all ({total})
                        </Button>
                    </div>
                </div>

                <div className="rounded-lg ring-1 ring-slate-200 overflow-hidden bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="backfill-table">
                            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                                <tr>
                                    <th className="px-3 py-2 text-left w-10">
                                        <input
                                            type="checkbox"
                                            checked={allSelectedOnPage}
                                            onChange={toggleAllOnPage}
                                            data-testid="backfill-header-checkbox"
                                        />
                                    </th>
                                    <th className="px-3 py-2 text-left">Customer</th>
                                    <th className="px-3 py-2 text-left">Unit #</th>
                                    <th className="px-3 py-2 text-left">Equipment</th>
                                    <th className="px-3 py-2 text-left">Start</th>
                                    <th className="px-3 py-2 text-left">Released</th>
                                    <th className="px-3 py-2 text-right">Monthly</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                                            <Loader2 className="inline animate-spin mr-2" size={14} />
                                            Loading…
                                        </td>
                                    </tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                                            <CheckCircle2 className="inline mr-2 text-emerald-600" size={14} />
                                            No unscheduled rentals in this window — you&apos;re all caught up.
                                        </td>
                                    </tr>
                                )}
                                {items.map((r) => (
                                    <tr
                                        key={r.rental_id}
                                        data-testid={`backfill-row-${r.rental_id}`}
                                        className={
                                            "hover:bg-slate-50/70 " +
                                            (selectedIds.has(r.rental_id) ? "bg-blue-50/40" : "")
                                        }
                                    >
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(r.rental_id)}
                                                onChange={() => toggleOne(r.rental_id)}
                                                data-testid={`backfill-checkbox-${r.rental_id}`}
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            <Link
                                                to={`/admin/customers/${r.customer_id}`}
                                                className="text-blue-800 hover:underline"
                                            >
                                                {r.customer_name}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-slate-700">
                                            <Link
                                                to={`/admin/rentals/${r.rental_id}`}
                                                className="hover:underline"
                                            >
                                                {r.unit_number || "—"}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">
                                            {EQUIPMENT_LABEL[r.equipment_type] || r.equipment_type || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-slate-600">{_fmtDate(r.start_date)}</td>
                                        <td className="px-3 py-2 text-slate-600">{_fmtDate(r.released_at)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-slate-900">
                                            {_fmtMoney(r.monthly_rate)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {!loading && total > LIMIT && (
                        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-xs text-slate-600">
                            <span>
                                Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
                            </span>
                            <div className="flex gap-1.5">
                                <Button
                                    size="sm" variant="outline"
                                    disabled={offset === 0}
                                    onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                                >Prev</Button>
                                <Button
                                    size="sm" variant="outline"
                                    disabled={offset + LIMIT >= total}
                                    onClick={() => setOffset(offset + LIMIT)}
                                >Next</Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="rounded-lg ring-1 ring-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                            <Info size={14} />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-slate-900">Backfill settings</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                These apply to every selected rental. day_of_month defaults to each rental&apos;s
                                start_date (clamped to 1–28); monthly amount = rental.monthly_rate.
                            </p>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 pl-11">
                        <div className="rounded-md ring-1 ring-slate-200 bg-slate-50 px-3 py-3 flex items-start gap-3">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">Prorate first invoice</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    If rental started mid-period, charge prorated amount for the partial first month.
                                </p>
                            </div>
                            <Switch
                                checked={prorateFirst}
                                onCheckedChange={setProrateFirst}
                                data-testid="backfill-prorate"
                            />
                        </div>
                        <div className="rounded-md ring-1 ring-slate-200 bg-slate-50 px-3 py-3">
                            <Label htmlFor="starts-offset" className="text-sm font-medium text-slate-900">
                                Start billing in
                            </Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    id="starts-offset" type="number" min={0} max={30}
                                    value={startsAtOffsetDays}
                                    onChange={(e) => setStartsAtOffsetDays(Math.max(0, Math.min(30, Number(e.target.value))))}
                                    className="w-20 h-9"
                                    data-testid="backfill-offset"
                                />
                                <span className="text-sm text-slate-700">day(s) from now</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                Schedules will use <span className="font-mono">starts_at = {targetStartDate}</span>.
                                Buffer time lets you verify before invoices fire.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white/90 backdrop-blur ring-1 ring-slate-200 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-sm text-slate-700">
                        <span data-testid="backfill-selection-count" className="font-semibold">
                            {selectedIds.size}
                        </span>{" "}
                        rental{selectedIds.size === 1 ? "" : "s"} selected
                    </span>
                    <Button
                        type="button"
                        disabled={selectedIds.size === 0 || submitting}
                        onClick={() => setConfirmOpen(true)}
                        className="bg-emerald-700 hover:bg-emerald-800"
                        data-testid="backfill-cta"
                    >
                        Create {selectedIds.size || ""} billing schedule{selectedIds.size === 1 ? "" : "s"}
                    </Button>
                </div>
            </div>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="backfill-confirm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle size={16} className="text-amber-600" />
                            Confirm bulk schedule creation
                        </DialogTitle>
                        <DialogDescription>
                            This is a bulk operation. Please review before submitting.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>
                            Will create{" "}
                            <span className="font-semibold">{selectedIds.size}</span> billing
                            schedule{selectedIds.size === 1 ? "" : "s"}.
                        </p>
                        <ul className="text-xs text-slate-600 list-disc pl-5 space-y-1">
                            <li>
                                <span className="font-mono">starts_at</span> = {targetStartDate} (
                                {startsAtOffsetDays} day{startsAtOffsetDays === 1 ? "" : "s"} from now)
                            </li>
                            <li>First invoices will fire on each rental&apos;s day_of_month (clamped to 1–28).</li>
                            <li>
                                Prorate first invoice:{" "}
                                <span className="font-mono">{prorateFirst ? "yes" : "no"}</span>
                            </li>
                            <li>
                                <span className="font-mono">auto_charge</span> = off (you must enable per
                                schedule with a verified payment method)
                            </li>
                        </ul>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button" variant="outline"
                            onClick={() => setConfirmOpen(false)}
                            disabled={submitting}
                        >Cancel</Button>
                        <Button
                            type="button"
                            onClick={onSubmit}
                            disabled={submitting}
                            className="bg-emerald-700 hover:bg-emerald-800"
                            data-testid="backfill-confirm-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={14} />
                                    Creating…
                                </>
                            ) : (
                                "Create schedules"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardShell>
    );
}
